import * as libphonenumber from 'google-libphonenumber';
import * as yup from 'yup';

import {
  ACCESS_FIELD_MAX_LENGTH,
  ACCESS_FIELD_MIN_LENGTH,
  ACCESS_FIELD_REGEX,
  COMPANY_MAX_LENGTH,
  COMPANY_MIN_LENGTH,
  COMPANY_VALID_CHARS_REGEX,
  CONFIRMATION_CODE_LENGTH,
  CONFIRMATION_CODE_REGEX,
  EMAIL_REG_EXP,
} from '../constants/index.js';

import {
  arePasswordRulesSatisfied,
  evaluatePasswordRules,
  type PersonalInfoContext,
} from './password-rules.js';

const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();

const optionalAccessFieldSchema = yup
  .string()
  .trim()
  .test(
    'access-field-min-length',
    `Value must be at least ${ACCESS_FIELD_MIN_LENGTH} characters`,
    (value) => !value || value.length >= ACCESS_FIELD_MIN_LENGTH
  )
  .test(
    'access-field-max-length',
    `Value must be less than ${ACCESS_FIELD_MAX_LENGTH} characters`,
    (value) => !value || value.length <= ACCESS_FIELD_MAX_LENGTH
  )
  .test('access-field-format', 'Only latin letters, numbers, and spaces are allowed', (value) => {
    return !value || ACCESS_FIELD_REGEX.test(value);
  });

export type AccessFormData = {
  requestedAccessApplication?: string;
  requestedRole?: string;
};

export const userInfoSchema = yup.object({
  name: yup
    .string()
    .required('Name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name must be less than 50 characters'),

  companyName: yup
    .string()
    .required('Company name is required')
    .min(COMPANY_MIN_LENGTH, 'Company name must be at least 2 characters')
    .max(COMPANY_MAX_LENGTH, 'Company name must be less than 100 characters')
    .matches(
      COMPANY_VALID_CHARS_REGEX,
      "Only letters, numbers, spaces, and special characters (&, ., ,, -, ', /, +) are allowed"
    ),

  email: yup.string().required('Email is required').matches(EMAIL_REG_EXP, 'Wrong email format.'),

  phoneNumber: yup
    .string()
    .required('Phone number is required')
    .test('valid-phone', 'Please enter a valid phone number', (value) => {
      if (!value) {
        return false;
      }
      try {
        const parsedNumber = phoneUtil.parse(value);
        return phoneUtil.isValidNumber(parsedNumber);
      } catch {
        return false;
      }
    }),
});

export const confirmationCodeSchema = yup.object({
  confirmationCode: yup
    .string()
    .required('Confirmation code is required')
    .matches(CONFIRMATION_CODE_REGEX, 'Confirmation code must be exactly 6 digits')
    .length(CONFIRMATION_CODE_LENGTH, 'Confirmation code must be exactly 6 digits'),
});

export const accessSchema: yup.ObjectSchema<AccessFormData> = yup.object({
  requestedAccessApplication: optionalAccessFieldSchema.optional(),
  requestedRole: optionalAccessFieldSchema.optional(),
});

export const createPasswordSchema = (userInfo?: PersonalInfoContext) =>
  yup.object({
    password: yup
      .string()
      .required('Password requirements not met')
      .test('password-requirements', 'Password requirements not met', (value) => {
        if (!value) {
          return false;
        }
        return arePasswordRulesSatisfied(evaluatePasswordRules(value, userInfo));
      }),

    confirmPassword: yup
      .string()
      .required('Entered passwords did not match')
      .oneOf([yup.ref('password')], 'Entered passwords did not match'),
  });

export const passwordSchema = createPasswordSchema();

export type UserInfoFormData = yup.InferType<typeof userInfoSchema>;
export type ConfirmationCodeFormData = yup.InferType<typeof confirmationCodeSchema>;
export type PasswordFormData = yup.InferType<typeof passwordSchema>;
